import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startGuardarTextosLegales, startLoadTextosLegales } from '../../../actions/textLegal';
import { openModal } from '../../../actions/ui';
import {
  AMBITO_GLOBAL,
  TIPOS_TEXTO_LEGAL,
  conservarBorradoresPendientes,
  construirBodyGuardado,
  construirBorradores,
  detectarAmbitosModificados,
  obtenerTiposVaciosDelGlobal,
  sucursalTieneTextoPropio,
} from '../../../helpers/textosLegales';
import TextEditor from './editor/TextEditor';
import { SelectorAmbitoLegal } from './legal/SelectorAmbitoLegal';

// Quill avisa también de los cambios que hace él al normalizar el HTML que recibe ('api'). Solo
// cuenta lo que escribe el usuario: un aviso legal normalizado se guardaría como cambiado y el
// backend pediría a todos los clientes aceptarlo de nuevo.
const ORIGEN_CAMBIO_USUARIO = 'user';
const CARGA_EN_CURSO = 'cargando';
const CARGA_FALLIDA = 'error';
const CARGA_RESUELTA = 'resuelta';
const MINIMO_SUCURSALES_CON_SELECTOR = 2;
const NOMBRE_AMBITO_GLOBAL = 'Global';

const claveDeTexto = (ambito, campo) => `${ambito}:${campo}`;

const describirTiposVacios = (tiposVacios, hayTextosPorSucursal) => {
  const listaDeTipos = tiposVacios.join(', ');
  if (hayTextosPorSucursal) return `Los siguientes textos globales son obligatorios: ${listaDeTipos}`;
  return `Los siguientes campos son obligatorios: ${listaDeTipos}`;
};

export const Legal = () => {
  const dispatch = useDispatch();
  const { colores } = useSelector(state => state.ui);
  const { textosLegales } = useSelector(state => state.textLegal);

  const [estadoCarga, setEstadoCarga] = useState(CARGA_EN_CURSO);
  const [intentoDeCarga, setIntentoDeCarga] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [campoActivo, setCampoActivo] = useState(TIPOS_TEXTO_LEGAL[0].campo);
  const [ambitoElegido, setAmbitoElegido] = useState(AMBITO_GLOBAL);
  const [borradores, setBorradores] = useState(() => construirBorradores(null));
  // Si el editor de cada texto de sucursal está abierto, por encima de lo que diga su contenido:
  // borrar el texto para reescribirlo no debe cerrar el editor a mitad.
  const [editoresPropiosAbiertos, setEditoresPropiosAbiertos] = useState({});
  const originalAnteriorRef = useRef(null);
  const componenteMontadoRef = useRef(true);

  const original = useMemo(() => construirBorradores(textosLegales), [textosLegales]);
  const sucursales = textosLegales?.sucursales || [];
  const mostrarSelectorAmbito = sucursales.length >= MINIMO_SUCURSALES_CON_SELECTOR;
  const ambitoElegidoExiste = Boolean(borradores[ambitoElegido]);
  const puedeEditarAmbitoElegido = mostrarSelectorAmbito && ambitoElegidoExiste;
  const ambitoActivo = puedeEditarAmbitoElegido ? ambitoElegido : AMBITO_GLOBAL;
  const esAmbitoGlobal = ambitoActivo === AMBITO_GLOBAL;
  const claveActiva = claveDeTexto(ambitoActivo, campoActivo);
  const ambitosModificados = detectarAmbitosModificados(original, borradores);
  const puedeAplicar = estadoCarga === CARGA_RESUELTA && ambitosModificados.length > 0;

  useEffect(() => {
    componenteMontadoRef.current = true;
    return () => { componenteMontadoRef.current = false; };
  }, []);

  useEffect(() => {
    setEstadoCarga(CARGA_EN_CURSO);
    dispatch(startLoadTextosLegales()).then((seHanLeido) => {
      if (!componenteMontadoRef.current) return;
      setEstadoCarga(seHanLeido ? CARGA_RESUELTA : CARGA_FALLIDA);
    });
  }, [dispatch, intentoDeCarga]);

  useEffect(() => {
    const originalAnterior = originalAnteriorRef.current;
    originalAnteriorRef.current = original;
    setBorradores(borradoresActuales => conservarBorradoresPendientes(originalAnterior, original, borradoresActuales));
    setEditoresPropiosAbiertos({});
  }, [original]);

  const actualizarBorrador = (ambito, campo, texto) => {
    setBorradores(borradoresActuales => ({
      ...borradoresActuales,
      [ambito]: { ...borradoresActuales[ambito], [campo]: texto },
    }));
  };

  const marcarEditorPropio = (estaAbierto) => {
    setEditoresPropiosAbiertos(editoresActuales => ({ ...editoresActuales, [claveActiva]: estaAbierto }));
  };

  const editarTexto = (textoEditado, delta, origenDelCambio) => {
    if (origenDelCambio !== ORIGEN_CAMBIO_USUARIO) return;

    actualizarBorrador(ambitoActivo, campoActivo, textoEditado);
    if (!esAmbitoGlobal) marcarEditorPropio(true);
  };

  const escribirTextoPropio = () => {
    actualizarBorrador(ambitoActivo, campoActivo, borradores[AMBITO_GLOBAL][campoActivo]);
    marcarEditorPropio(true);
  };

  const usarTextoGlobal = () => {
    actualizarBorrador(ambitoActivo, campoActivo, '');
    marcarEditorPropio(false);
  };

  const globalEstaCompleto = () => {
    const tiposVaciosDelGlobal = obtenerTiposVaciosDelGlobal(borradores[AMBITO_GLOBAL]);
    if (tiposVaciosDelGlobal.length === 0) return true;

    dispatch(openModal({
      title: 'Campos obligatorios',
      text: [describirTiposVacios(tiposVaciosDelGlobal, mostrarSelectorAmbito)],
      buttonText: 'Cerrar'
    }));
    return false;
  };

  const aplicarCambios = async () => {
    if (guardando || !globalEstaCompleto()) return;

    const cuerposGuardado = ambitosModificados.map(ambito => construirBodyGuardado(ambito, borradores[ambito]));
    setGuardando(true);
    await dispatch(startGuardarTextosLegales(cuerposGuardado));
    if (componenteMontadoRef.current) setGuardando(false);
  };

  const reintentarCarga = () => setIntentoDeCarga(intento => intento + 1);

  const cancelarCambios = () => {
    setBorradores(original);
    setEditoresPropiosAbiertos({});
  };

  const opcionesDeAmbito = [
    { ambito: AMBITO_GLOBAL, nombre: NOMBRE_AMBITO_GLOBAL, tieneTextoPropio: false },
    ...sucursales.map(({ sucursal, nombre }) => ({
      ambito: sucursal,
      nombre,
      tieneTextoPropio: sucursalTieneTextoPropio(borradores, sucursal, campoActivo),
    })),
  ];

  const renderizarEditor = () => (
    <TextEditor
      key={claveActiva}
      value={borradores[ambitoActivo][campoActivo]}
      onChange={editarTexto}
    />
  );

  const renderizarTextoGlobal = () => (
    <>
      {mostrarSelectorAmbito && (
        <p className='legal-ayuda'>Lo usan todas las sucursales que no tienen un texto propio.</p>
      )}
      {renderizarEditor()}
    </>
  );

  const renderizarTextoDeSucursal = () => {
    const editorPropioAbierto = editoresPropiosAbiertos[claveActiva]
      ?? sucursalTieneTextoPropio(borradores, ambitoActivo, campoActivo);

    if (!editorPropioAbierto) {
      return (
        <div className='legal-aviso'>
          <p>Esta sucursal usa el texto global.</p>
          <button type='button' className='legal-accion' style={{ color: colores.colorPrincipal }} onClick={escribirTextoPropio}>
            Escribir un texto propio
          </button>
        </div>
      );
    }

    return (
      <>
        <div className='legal-aviso'>
          <p>Texto propio de esta sucursal: sustituye al global solo aquí.</p>
          <button type='button' className='legal-accion' style={{ color: colores.colorPrincipal }} onClick={usarTextoGlobal}>
            Usar el texto global
          </button>
        </div>
        {renderizarEditor()}
      </>
    );
  };

  const renderizarContenido = () => {
    if (estadoCarga === CARGA_EN_CURSO) return <p className='legal-ayuda'>Cargando los textos legales...</p>;

    if (estadoCarga === CARGA_FALLIDA) {
      return (
        <div className='legal-aviso'>
          <p>No se han podido cargar los textos legales.</p>
          <button type='button' className='legal-accion' style={{ color: colores.colorPrincipal }} onClick={reintentarCarga}>
            Reintentar
          </button>
        </div>
      );
    }

    return esAmbitoGlobal ? renderizarTextoGlobal() : renderizarTextoDeSucursal();
  };

  return (
    <div className='configuracion-wrapper'>
      <div className='header'>
        <h1 style={{ color: colores.colorPrincipal }}>Legal</h1>
        {puedeAplicar && (
          <div className='botonera'>
            <button
              className='btn btn-primary'
              style={{ color: colores.color6, backgroundColor: colores.colorPrincipal }}
              onClick={aplicarCambios}
              disabled={guardando}
            >
              Aplicar
            </button>
            <button
              className='btn btn-secondary'
              style={{ color: colores.colorPrincipal, backgroundColor: colores.color6 }}
              onClick={cancelarCambios}
              disabled={guardando}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      <div className='customize-body'>
        <div className='left legal-tabs'>
          <div className='tabs'>
            {TIPOS_TEXTO_LEGAL.map(({ campo, etiqueta }) => {
              const esPestanaActiva = campo === campoActivo;
              return (
                <div
                  key={campo}
                  className={`tab ${esPestanaActiva ? 'active' : ''}`}
                  style={{ color: esPestanaActiva ? colores.colorPrincipal : colores.colorSecundario }}
                  onClick={() => setCampoActivo(campo)}
                >
                  {etiqueta}
                </div>
              );
            })}
          </div>

          {mostrarSelectorAmbito && estadoCarga === CARGA_RESUELTA && (
            <SelectorAmbitoLegal
              opciones={opcionesDeAmbito}
              ambitoActivo={ambitoActivo}
              onElegirAmbito={setAmbitoElegido}
              colores={colores}
            />
          )}

          <div className='panel-body left-body'>
            {renderizarContenido()}
          </div>
        </div>
      </div>
    </div>
  )
}
